import { isAuthenticated } from "../utils/auth";
import { getUserRole } from "../utils/sessionStore";
import React, { useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import { FiArrowRight } from 'react-icons/fi';

const cardHoverSx = {
  cursor: 'pointer',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to right, #0048e6, #9C15F7, rgba(255, 75, 248, 0.3))',
    opacity: 0,
    zIndex: 0,
    transition: 'opacity 0.25s ease',
  },
  '&:hover': {
    '&:before': {
      opacity: 0.28,
    },
  },
};

const styles = {
  pageWrap: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  embeddedWrap: {
    width: '100%',
  },
  embeddedHeaderSection: {
    padding: '0 0 1.25rem',
    background: 'transparent',
    borderBottom: 'none',
    marginBottom: '0.25rem',
  },
  embeddedMainContent: {
    width: '100%',
    padding: '0.5rem 0 2rem',
  },
  headerLeading: {
    flexShrink: 0,
    marginBottom: 0,
  },
  headerTrailing: {
    flexShrink: 0,
    marginBottom: 0,
    marginLeft: 'auto',
    alignSelf: 'center',
  },
  headerSection: {
    padding: '2rem 1.5rem 1.5rem',
    background:
      'linear-gradient(180deg, rgba(15, 23, 42, 0.03) 0%, transparent 100%)',
    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    maxWidth: 1200,
    margin: '0 auto',
  },
  headerTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  pageTitle: {
    fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.02em',
    margin: 0,
    lineHeight: 1.2,
  },
  pageSubtitle: {
    fontSize: 'clamp(0.9rem, 2vw, 1rem)',
    color: '#64748b',
    marginTop: '0.35rem',
    fontWeight: 400,
  },
  mainContent: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: '1.5rem',
    paddingBottom: '3rem',
  },
  cardContainer: {
    justifyContent: 'flex-start',
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    height: '100%',
  },
  card: {
    height: '100%',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    backgroundColor: '#ffffff',
  },
  cardContent: {
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    height: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 1,
  },
  iconWrap: {
    width: 52,
    height: 52,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
  },
  cardTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#0f172a',
    margin: 0,
    marginBottom: '0.35rem',
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  cardDescription: {
    fontSize: '0.875rem',
    color: '#64748b',
    lineHeight: 1.45,
    marginBottom: '1rem',
    flex: 1,
  },
  cardArrow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#475569',
    opacity: 0.85,
  },
  arrowText: {
    margin: 0,
  },
};

export const dashboardGridStyles = styles;

function DashboardGridCard({ card, onClick, useLink, linkTo }) {
  const cardInner = (
    <Card style={styles.card} onClick={onClick} sx={cardHoverSx}>
      <CardContent style={styles.cardContent}>
        <Box
          className="card-icon-wrap"
          style={{
            ...styles.iconWrap,
            color: card.accent,
            backgroundColor: `${card.accent}14`,
          }}
        >
          {card.icon}
        </Box>
        <Typography component="h3" style={styles.cardTitle}>
          {card.title}
        </Typography>
        <Typography style={styles.cardDescription}>
          {card.description}
        </Typography>
        <Box className="card-arrow" style={styles.cardArrow}>
          <span style={styles.arrowText}>Open</span>
          <FiArrowRight size={16} style={{ flexShrink: 0 }} />
        </Box>
      </CardContent>
    </Card>
  );

  if (useLink) {
    return (
      <Link to={linkTo} style={styles.link}>
        {cardInner}
      </Link>
    );
  }

  return cardInner;
}

/**
 * Shared dashboard grid used by home, global settings, DAR hubs, and embedded hubs.
 * @param {boolean} [filterByRole=true] — when false, all cards are shown
 * @param {(card: object) => void} [onCardClick] — callback mode instead of route navigation
 * @param {boolean} [embedded=false] — lighter wrapper when nested inside another page
 * @param {React.ReactNode} [headerLeading] — optional control row above the title (e.g. back button)
 * @param {React.ReactNode} [headerTrailing] — optional control on the right side of the header
 */
export default function DashboardCardGrid({
  pageTitle,
  pageSubtitle,
  cardData,
  onCardClick,
  filterByRole = true,
  embedded = false,
  headerLeading,
  headerTrailing,
}) {
  const navigate = useNavigate();
  const callbackMode = typeof onCardClick === 'function';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNavigate = (path) => {
    if (isAuthenticated()) {
      navigate(path);
    } else {
      alert('You must be logged in to access this page.');
    }
  };

  const userRole = getUserRole();
  const filteredCards = filterByRole
    ? cardData.filter((card) => card.roles?.includes(userRole))
    : cardData;

  const fadeInUp = keyframes`
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  const wrapStyle = embedded ? styles.embeddedWrap : styles.pageWrap;

  return (
    <Box style={wrapStyle}>
      <Box style={embedded ? styles.embeddedHeaderSection : styles.headerSection}>
        <Box style={styles.headerRow}>
          {headerLeading ? <Box style={styles.headerLeading}>{headerLeading}</Box> : null}
          <Box style={styles.headerTextBlock}>
            <Typography component="h1" style={styles.pageTitle}>
              {pageTitle}
            </Typography>
            <Typography style={styles.pageSubtitle}>{pageSubtitle}</Typography>
          </Box>
          {headerTrailing ? <Box style={styles.headerTrailing}>{headerTrailing}</Box> : null}
        </Box>
      </Box>

      <Box style={embedded ? styles.embeddedMainContent : styles.mainContent}>
        <Grid
          container
          spacing={3}
          style={styles.cardContainer}
          sx={{
            animation: `${fadeInUp} 0.5s ease-out forwards`,
          }}
        >
          {filteredCards.map((card, index) => {
            const cardKey = card.id ?? card.path ?? index;
            const useLink = !callbackMode && isAuthenticated();
            const handleClick = callbackMode
              ? () => onCardClick(card)
              : useLink
                ? undefined
                : () => handleNavigate(card.path);

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={cardKey}
                sx={{
                  animation: `${fadeInUp} 0.4s ease-out ${index * 0.05}s forwards`,
                  opacity: 0,
                }}
              >
                <DashboardGridCard
                  card={card}
                  onClick={handleClick}
                  useLink={useLink}
                  linkTo={card.path}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}
